/**
 * Dashboard Analytics Types
 *
 * UI-specific type definitions for dashboard filters.
 * API request/response types are imported from @skewb-works/contracts.
 */

import type { AnalyticsQuery } from '@skewb-works/contracts';

/**
 * Date range filter options
 */
export type DateRangeFilter = 'last30' | 'last90' | 'last6m' | 'ytd' | 'all';

/**
 * Priority filter options (priority id as string or 'all')
 */
export type PriorityFilter = string;

/**
 * Dashboard filter state (UI state with "all" options)
 *
 * This differs from AnalyticsQuery because it includes "all" as a valid value
 * for filters, which is a UI concern. The API doesn't accept "all" - it
 * expects the filter to be omitted instead.
 */
export interface DashboardFilters {
  dateRange: DateRangeFilter;
  jobType: string;
  assignee: string;
  priority: PriorityFilter;
}

/**
 * Helper to convert dashboard filters to API query params
 *
 * Returns AnalyticsQuery type from the contract to ensure type safety.
 * Omits 'all' values to avoid sending unnecessary parameters.
 */
export function filtersToQueryParams(filters: DashboardFilters): AnalyticsQuery {
  const params: AnalyticsQuery = {};

  if (filters.dateRange) {
    params.dateRange = filters.dateRange;
  }

  if (filters.jobType && filters.jobType !== 'all') {
    params.jobType = filters.jobType;
  }

  if (filters.assignee && filters.assignee !== 'all') {
    params.assignee = filters.assignee;
  }

  if (filters.priority && filters.priority !== 'all') {
    params.priority = filters.priority;
  }

  return params;
}
